import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "@/lib/clientErrors";

/**
 * v2.2 P1.3: a render error shows a way back instead of a blank page, and is reported to the ledger.
 *
 * Weekly audit 2026-10-26: a tab open across a deploy asks for the old build's lazy chunk, which the
 * deploy removed. A reload is the whole fix, so the boundary does it once by itself; a second such
 * failure inside STALE_RELOAD_MS is a real one and gets the card and the report.
 */
const STALE_CHUNK =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;
const STALE_RELOAD_KEY = "landcrm:stale-chunk-reload-at";
const STALE_RELOAD_MS = 30_000;

function claimStaleChunkReload(now: number = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(STALE_RELOAD_KEY) ?? 0);
    if (now - last < STALE_RELOAD_MS) return false;
    sessionStorage.setItem(STALE_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false; // no storage, no guard against a loop: show the card
  }
}

export class ErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean; reloading: boolean }
> {
  override state = { failed: false, reloading: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    if (STALE_CHUNK.test(error.message) && claimStaleChunkReload()) {
      this.setState({ reloading: true });
      window.location.reload();
      return;
    }
    reportClientError({
      message: `${error.name}: ${error.message}`,
      stack: `${error.stack ?? ""}\n--- component stack ---${info.componentStack ?? ""}`,
      kind: "boundary",
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    if (this.state.reloading) {
      return <p className="mx-auto mt-24 max-w-md text-sm text-fg-muted">Loading the new version…</p>;
    }
    return (
      <div role="alert" className="mx-auto mt-24 max-w-md rounded-[var(--radius-card)] border bg-card p-6 text-sm">
        <p className="font-medium">Something on this page broke.</p>
        <p className="mt-1 text-fg-muted">It has been reported. Reload to carry on.</p>
        <button
          type="button"
          className="mt-4 rounded border px-3 py-1.5 font-medium hover:bg-surface-2"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}
